import { useEffect, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import jsPDF from "jspdf";
import { useParams } from "react-router-dom";
import { supabase } from "./lib/supabaseClient";
import { useToast } from "./ToastContext";
import { Country } from "country-state-city";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import UpgradeModal from "./components/UpgradeModal";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import BrandKit from "./components/BrandKit";

window.onerror = function (msg, url, line, col, error) {
  console.error("GLOBAL ERROR:", error);
};

function ColorInput({ label, caption, value, onChange, dataKit }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <label className="text-[9px] text-gray-500 uppercase font-black">{label}</label>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-kit={dataKit}
        className="w-10 h-10 rounded-lg cursor-pointer border-none bg-transparent"
      />
      <span className="text-[8px] text-gray-600">{caption}</span>
    </div>
  );
}

const getActiveRoute = (routes) => {
  const device =
    typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent)
      ? "ios"
      : typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent)
      ? "android"
      : "other";
  const userLocale = typeof navigator !== "undefined" ? navigator.language : "en-IN";
  const userCountry = userLocale.split("-")[1] || "IN";
  for (let route of routes) {
    if (route.is_active === false) continue;
    if (route.device && route.device !== device) continue;
    if (route.country_code && route.country_code !== userCountry) continue;
    const tz = route.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = moment().tz(tz);
    if (!route.start_time || !route.end_time) continue;
    const nowMinutes = now.hours() * 60 + now.minutes();
    const [sh, sm] = route.start_time.split(":").map(Number);
    const [eh, em] = route.end_time.split(":").map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    if (nowMinutes >= start && nowMinutes <= end) return route.destination_url;
  }
  return null;
};

// ─────────────────────────────────────────────
// CORE EXPORT FUNCTION: canvas-based, no html2canvas
// Generates QR → draws frame → draws logo → exports
// ─────────────────────────────────────────────
async function generateExportCanvas({ size, data, dotsColor, bgColor, eyesColor, dotsType, cornersType, cornersDotType, logo, designer }) {
  // 1. Generate QR as blob using QRCodeStyling
  const qrLib = new QRCodeStyling({
    width: size,
    height: size,
    type: "canvas",
    data: data || "https://qr.digitoly.com",
    dotsOptions: { color: dotsColor, type: dotsType },
    backgroundOptions: { color: bgColor || "#ffffff" },
    cornersSquareOptions: { color: eyesColor, type: cornersType },
    cornersDotOptions: { color: eyesColor, type: cornersDotType },
    // No image here — we draw the logo ourselves on the final canvas
    // This avoids cross-origin canvas taint entirely
  });

  const qrBlob = await qrLib.getRawData("png");
  if (!qrBlob) throw new Error("QR generation failed");

  // 2. Load QR blob as image
  const qrImg = await loadImage(URL.createObjectURL(qrBlob));

  const hasFrame = designer?.frame && designer.frame !== "none";
  // Frame text banner goes BELOW the QR — never clips it
  const FRAME_HEIGHT = hasFrame ? Math.round(size * 0.14) : 0;
  const BORDER = hasFrame ? Math.round(size * 0.022) : 0;
  const canvasW = size + BORDER * 2;
  const canvasH = size + BORDER * 2 + FRAME_HEIGHT; // extra height at BOTTOM

  // 3. Create output canvas
  const canvas = document.createElement("canvas");
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext("2d");

  // 4. Fill background white
  ctx.fillStyle = bgColor || "#ffffff";
  ctx.fillRect(0, 0, canvasW, canvasH);

  // helper: rounded rect path
  const rr = (x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  // 5. Draw frame
  if (hasFrame) {
    const fc = designer.frameColor || "#000000";
    const outerRadius = Math.round(size * 0.05);
    const innerRadius = Math.round(size * 0.03);

    // Outer frame background (full canvas rounded rect)
    ctx.fillStyle = fc;
    rr(0, 0, canvasW, canvasH, outerRadius);
    ctx.fill();

    // White QR area — positioned at TOP with BORDER padding
    // QR sits at y=BORDER, NOT pushed down by frame height
    ctx.fillStyle = bgColor || "#ffffff";
    rr(BORDER, BORDER, size, size, innerRadius);
    ctx.fill();
  }

  // 6. Draw QR image — always at top (y = BORDER)
  const qrX = BORDER;
  const qrY = BORDER;
  if (designer?.qrShape === "circle") {
    ctx.save();
    ctx.beginPath();
    ctx.arc(qrX + size / 2, qrY + size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(qrImg, qrX, qrY, size, size);
    ctx.restore();
  } else {
    ctx.drawImage(qrImg, qrX, qrY, size, size);
  }

  // 7. Draw logo in center of QR
  if (logo) {
    try {
      const logoImg = await loadImage(logo);
      const logoSize = Math.round(size * 0.2);
      const logoX = qrX + (size - logoSize) / 2;
      const logoY = qrY + (size - logoSize) / 2;
      ctx.save();
      ctx.fillStyle = "#ffffff";
      const pad = Math.round(logoSize * 0.15);
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2 + pad, 0, Math.PI * 2);
      ctx.fill();
      ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
      ctx.restore();
    } catch (e) {
      console.warn("Logo draw failed:", e);
    }
  }

  // 8. Draw frame text in banner at BOTTOM
  // Banner occupies: from (BORDER + size) to canvasH
  if (hasFrame && FRAME_HEIGHT > 0) {
    const text = designer.frameText || "SCAN ME";
    const textColor = designer.textColor || "#ffffff";
    const fontSize = Math.round(FRAME_HEIGHT * 0.38);
    ctx.fillStyle = textColor;
    ctx.font = `900 ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // Center of the bottom banner
    const bannerCenterY = BORDER + size + FRAME_HEIGHT / 2;
    ctx.fillText(text.toUpperCase(), canvasW / 2, bannerCenterY);
  }

  return canvas;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => {
      // Try without crossOrigin as fallback
      const img2 = new Image();
      img2.onload = () => resolve(img2);
      img2.onerror = reject;
      img2.src = src;
    };
    img.src = src;
  });
}

export default function AdvancedGenerator({ user, existingQR, onSaveComplete, plan, limits }) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { id } = useParams();
  const ref = useRef(null);
  const qrCode = useRef(null);
  const { addToast } = useToast();
  const isEditMode = !!existingQR || !!id;
  const navigate = useNavigate();

  const handleCreateNew = () => {
    navigate("/dashboard/create", { replace: true });
    window.location.reload();
  };

  // --- CORE STATES ---
  const [campaignName, setCampaignName] = useState(existingQR?.name || "");
  const [url, setUrl] = useState(existingQR?.destination_url || "");
  const [qrType, setQrType] = useState(existingQR?.is_dynamic ? "dynamic" : "static");
  const [couponMode, setCouponMode] = useState(existingQR?.coupon_mode || false);
  const [qrMode, setQrMode] = useState(existingQR?.qr_mode || "direct");
  const [expiresAt, setExpiresAt] = useState(existingQR?.expires_at || null);
  const [maxScans, setMaxScans] = useState(existingQR?.max_scans || null);

  const [wifiData, setWifiData] = useState({
    ssid: existingQR?.wifi_ssid || "",
    password: existingQR?.wifi_password || "",
    security: existingQR?.wifi_security || "WPA",
    hidden: existingQR?.wifi_hidden || false,
  });

  const [upiData, setUpiData] = useState({
    id: existingQR?.upi_id || "",
    name: existingQR?.upi_name || "",
    amount: existingQR?.upi_amount || "",
  });

  const [activeTab, setActiveTab] = useState("content");
  const [loading, setLoading] = useState(false);
  const qrCodeString = useRef(existingQR?.code || null);

  const getFlag = (countryCode) =>
    String.fromCodePoint(...[...countryCode.toUpperCase()].map(c => 127397 + c.charCodeAt()));

  // --- STYLE STATES ---
  const [dotsColor, setDotsColor] = useState(existingQR?.design?.dotsColor || "#000000");
  const [bgColor, setBgColor] = useState(existingQR?.design?.bgColor || "#ffffff");
  const [eyesColor, setEyesColor] = useState(existingQR?.design?.eyesColor || "#000000");
  const [dotsType, setDotsType] = useState(existingQR?.design?.dotsType || "square");
  const [cornersType, setCornersType] = useState(existingQR?.design?.cornersType || "extra-rounded");
  const [cornersDotType, setCornersDotType] = useState(existingQR?.design?.cornersDotType || "dot");
  const [logo, setLogo] = useState(existingQR?.design?.logo || null);

  // --- DESIGNER TAB STATE ---
  const [designer, setDesigner] = useState(existingQR?.design?.designer || {
    frame: "none",
    frameColor: "#000000",
    frameText: "SCAN ME",
    textColor: "#ffffff",
    qrShape: "square",
    borderStyle: "solid",
    exportSize: 1024
  });

  // --- ENTERPRISE SETTINGS ---
  const [routing, setRouting] = useState(existingQR?.settings?.routing || { ios: "", android: "" });
  const [pixels, setPixels] = useState(existingQR?.settings?.pixels || { fb: "", google: "" });
  const [pinCode, setPinCode] = useState(existingQR?.pin_code || "");
  const [abTesting, setAbTesting] = useState(existingQR?.ab_testing || false);
  const [abUrls, setAbUrls] = useState({ a: existingQR?.ab_url_a || "", b: existingQR?.ab_url_b || "" });
  const [timeRoutes, setTimeRoutes] = useState([]);
  const [fallback, setFallback] = useState({
    type: existingQR?.fallback_type || "url",
    value: existingQR?.fallback_value || ""
  });

  // --- VCARD & WHATSAPP ---
  const [vCard, setVCard] = useState({
    firstName: "", lastName: "", mobile: "", email: "", address: "", website: "",
    facebook: "", linkedin: "", instagram: "", twitter: "", youtube: "", profilePic: null,
    ...(existingQR?.settings?.vcard || {})
  });

  const [waData, setWaData] = useState(existingQR?.settings?.whatsapp || { number: "", message: "" });

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setLogo(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleProfilePic = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setVCard(prev => ({ ...prev, profilePic: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const getQRData = () => {
    const code = existingQR?.code || qrCodeString.current;
    const scanBase = "https://qr.digitoly.com/go";
    if (!code) return "";
    return `${scanBase}/${code}`;
  };

  useEffect(() => {
    if (qrMode === 'leads' || qrMode === 'feedback') setQrType('dynamic');
  }, [qrMode]);

  useEffect(() => {
    async function fetchQR() {
      const { data, error } = await supabase.from("qr_codes").select("*").eq("id", id).single();
      if (!error && data) {
        const { data: routes } = await supabase.from("qr_time_routes").select("*").eq("qr_id", id);
        if (routes) setTimeRoutes(routes);
        setCampaignName(data.name || "");
        setUrl(data.destination_url || "");
        setQrType(data.is_dynamic ? "dynamic" : "static");
        setQrMode(data.qr_mode || "direct");
        if (data.design) {
          setDotsColor(data.design.dotsColor || "#000000");
          setBgColor(data.design.bgColor || "#ffffff");
          setEyesColor(data.design.eyesColor || "#000000");
          setDotsType(data.design.dotsType || "square");
          setCornersType(data.design.cornersType || "extra-rounded");
          setCornersDotType(data.design.cornersDotType || "dot");
          setLogo(data.design?.logo || null);
          setDesigner(data.design.designer || designer);
          if (data.code) qrCodeString.current = data.code;
        }
        if (data.settings) {
          setVCard(data.settings.vcard || vCard);
          setWaData(data.settings.whatsapp || waData);
          setWifiData({
            ssid: data.wifi_ssid || "",
            password: data.wifi_password || "",
            security: data.wifi_security || "WPA",
            hidden: data.wifi_hidden || false
          });
          setUpiData({
            id: data.upi_id || "",
            name: data.upi_name || "",
            amount: data.upi_amount || "",
          });
        }
      }
    }
    if (id && !existingQR) fetchQR();
  }, [id, existingQR]);

  // Init QR preview
  useEffect(() => {
    qrCode.current = new QRCodeStyling({
      width: 300,
      height: 300,
      data: getQRData() || "https://qr.digitoly.com",
      dotsOptions: { color: dotsColor, type: dotsType },
      backgroundOptions: { color: bgColor },
      cornersSquareOptions: { color: eyesColor, type: cornersType },
      cornersDotOptions: { color: eyesColor, type: cornersDotType },
      image: logo || "",
      imageOptions: { crossOrigin: "anonymous", margin: 5, imageSize: 0.4 },
    });
    if (ref.current) qrCode.current.append(ref.current);
  }, []);

  // Update QR preview on any change
  useEffect(() => {
    if (qrCode.current) {
      qrCode.current.update({
        data: getQRData() || "https://qr.digitoly.com",
        dotsOptions: { color: dotsColor, type: dotsType },
        backgroundOptions: { color: bgColor },
        cornersSquareOptions: { color: eyesColor, type: cornersType },
        cornersDotOptions: { color: eyesColor, type: cornersDotType },
        image: logo || "",
        imageOptions: { crossOrigin: "anonymous", margin: 5, imageSize: 0.4 },
      });
    }
  }, [url, dotsColor, bgColor, eyesColor, dotsType, cornersType, cornersDotType, logo, qrMode, vCard, waData, wifiData, upiData, designer, routing, timeRoutes, existingQR, fallback]);

  // ─────────────────────────────────────────────
  // DOWNLOAD — uses canvas-based generator, no html2canvas
  // ─────────────────────────────────────────────
  const downloadQR = async (ext) => {
    const finalData = getQRData();
    if (!finalData) {
      addToast("Save the QR first before downloading", "error");
      return;
    }
    setLoading(true);
    try {
      const size = designer.exportSize || 1024;
      const fileName = campaignName || "digitoly-qr";
      const exportParams = { size, data: finalData, dotsColor, bgColor, eyesColor, dotsType, cornersType, cornersDotType, logo, designer };

      if (ext === "svg") {
        // SVG: use QR library directly (no frame/logo support in SVG — inform user)
        const tempQR = new QRCodeStyling({
          width: size, height: size,
          data: finalData,
          dotsOptions: { color: dotsColor, type: dotsType },
          backgroundOptions: { color: bgColor },
          cornersSquareOptions: { color: eyesColor, type: cornersType },
          cornersDotOptions: { color: eyesColor, type: cornersDotType },
          image: logo || "",
          imageOptions: { crossOrigin: "anonymous", margin: 5, imageSize: 0.4 },
        });
        tempQR.download({ name: fileName, extension: "svg" });
        addToast("SVG downloaded (frame not supported in SVG format)", "success");
        return;
      }

      // Generate canvas with QR + frame + logo all drawn natively
      const canvas = await generateExportCanvas(exportParams);

      if (ext === "png") {
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = `${fileName}-${size}px.png`; a.click();
          URL.revokeObjectURL(url);
          addToast(`PNG downloaded at ${size}px ✓`, "success");
        }, "image/png");
      } else if (ext === "jpg") {
        // JPG needs white background (no transparency)
        const jpgCanvas = document.createElement("canvas");
        jpgCanvas.width = canvas.width; jpgCanvas.height = canvas.height;
        const ctx = jpgCanvas.getContext("2d");
        ctx.fillStyle = bgColor || "#ffffff";
        ctx.fillRect(0, 0, jpgCanvas.width, jpgCanvas.height);
        ctx.drawImage(canvas, 0, 0);
        jpgCanvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = `${fileName}-${size}px.jpg`; a.click();
          URL.revokeObjectURL(url);
          addToast(`JPG downloaded at ${size}px ✓`, "success");
        }, "image/jpeg", 0.95);
      } else if (ext === "webp") {
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = `${fileName}-${size}px.webp`; a.click();
          URL.revokeObjectURL(url);
          addToast(`WEBP downloaded at ${size}px ✓`, "success");
        }, "image/webp", 0.92);
      }
    } catch (err) {
      console.error("Download failed:", err);
      addToast("Download failed: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    const finalData = getQRData();
    if (!finalData) { addToast("Save the QR first before downloading", "error"); return; }
    setLoading(true);
    try {
      const size = designer.exportSize || 1024;
      const exportParams = { size, data: finalData, dotsColor, bgColor, eyesColor, dotsType, cornersType, cornersDotType, logo, designer };
      const canvas = await generateExportCanvas(exportParams);
      const imgData = canvas.toDataURL("image/png");
      const pdfW = canvas.width;
      const pdfH = canvas.height;
      const pdf = new jsPDF({ orientation: pdfW > pdfH ? 'landscape' : 'portrait', unit: 'px', format: [pdfW, pdfH] });
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
      pdf.save(`${campaignName || "digitoly-qr"}.pdf`);
      addToast("PDF downloaded ✓", "success");
    } catch (err) {
      console.error("PDF failed:", err);
      addToast("PDF export failed: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

  const generateAndSave = async () => {
    if (!user?.id) { addToast("You must be logged in to save", "error"); return; }

    if (qrMode === "upi" && !upiData.id?.trim()) {
      addToast("Please enter your UPI ID first", "error");
      return;
    }

    if (!isEditMode && user?.id !== "39354205-3d56-466b-8aae-d90a069e5f77") {
      const { count } = await supabase.from("qr_codes").select("*", { count: "exact", head: true }).eq("user_id", user.id);
      const limit = limits?.qrCodes ?? 5;
      if (count >= limit) { setShowUpgrade(true); return; }
    }

    setLoading(true);
    try {
      const payload = {
        user_id: user.id, name: campaignName, destination_url: url,
        image_data: logo || null,
        qr_mode: qrMode,
        wifi_ssid: wifiData.ssid || null, wifi_password: wifiData.password || null,
        wifi_security: wifiData.security || "WPA", wifi_hidden: wifiData.hidden || false,
        expires_at: expiresAt || null, max_scans: maxScans ? parseInt(maxScans) : null,
        upi_id: upiData.id || null, upi_name: upiData.name || null,
        upi_amount: upiData.amount ? parseFloat(upiData.amount) : null,
        design: { dotsColor, bgColor, eyesColor, dotsType, cornersType, cornersDotType, logo, designer },
        settings: {
          vcard: { ...vCard, profilePic: vCard.profilePic || null },
          whatsapp: waData, routing, pixels, timezone: getUserTimezone(),
          lead_fields: [{ name: "name", label: "Full Name", required: true }, { name: "email", label: "Email", required: true }, { name: "phone", label: "Phone", required: false }]
        },
        fallback_type: fallback.type, coupon_mode: couponMode,
        meta_pixel_id: pixels.fb || "", google_pixel_id: pixels.google || "",
        pin_code: pinCode || null, ab_testing: abTesting,
        ab_url_a: abUrls.a || null, ab_url_b: abUrls.b || null,
        fallback_value: fallback.value
      };

      let response;
      if (isEditMode) {
        response = await supabase.from("qr_codes").update(payload).eq("id", existingQR?.id || id).select();
      } else {
        const newCode = Math.random().toString(36).substring(2, 8);
        qrCodeString.current = newCode;
        response = await supabase.from("qr_codes").insert([{ ...payload, code: newCode }]).select();
      }

      const { data, error } = response;
      if (error) throw error;

      const qrId = isEditMode ? (existingQR?.id || id) : data?.[0]?.id;
      const savedCode = isEditMode ? (existingQR?.code || qrCodeString.current) : data?.[0]?.code;
      qrCodeString.current = savedCode;

      if (qrCode.current) {
        qrCode.current.update({
          data: getQRData(),
          image: logo || "",
          imageOptions: { crossOrigin: "anonymous", margin: 5, imageSize: 0.4 },
        });
      }

      if (timeRoutes.length > 0 && qrId) {
        await supabase.from("qr_time_routes").delete().eq("qr_id", qrId);
        await supabase.from("qr_time_routes").insert(timeRoutes.map(slot => ({
          qr_id: qrId, start_time: slot.start_time, end_time: slot.end_time,
          destination_url: slot.destination_url, timezone: slot.timezone || getUserTimezone(),
          country_code: slot.country_code || null, device: slot.device || null, is_active: true
        })));
      }

      addToast("Asset Saved ✓", "success");
      if (onSaveComplete) onSaveComplete(); else navigate("/dashboard");
    } catch (err) {
      console.error("Save Error:", err.message);
      addToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-black to-slate-900 text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-black uppercase tracking-widest text-red-500">DIGITOLY QR PRO</h1>
            <span className="text-[12px] text-white-500 font-mono">DESIGNER SUITE V1.0</span>
          </div>

          <div className="flex gap-2 bg-black/20 p-1 rounded-xl w-fit">
            {['static', 'dynamic'].map(t => {
              const isLocked = (qrMode === 'leads' || qrMode === 'feedback') && t === 'static';
              return (
                <button key={t} onClick={() => { if (isLocked) { addToast("Leads & Feedback require Dynamic QR", "error"); return; } setQrType(t); }}
                  className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase ${qrType === t ? "bg-white text-black" : "text-gray-500"} ${isLocked ? "opacity-50 cursor-not-allowed" : ""}`}>{t}</button>
              );
            })}
          </div>

          <div className="flex gap-2 bg-black/20 p-1 rounded-xl">
            {['content', 'style', 'designer', 'advanced'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${activeTab === tab ? "bg-red-600 text-white" : "text-gray-500 hover:text-white"}`}>{tab}</button>
            ))}
          </div>

          {/* ===== CONTENT TAB ===== */}
          {activeTab === 'content' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4">
              {(qrMode === 'leads' || qrMode === 'feedback') && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-xl">
                  <p className="text-[10px] text-yellow-400">⚠ Leads & Feedback campaigns require Dynamic QR.</p>
                </div>
              )}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {['direct', 'vcard', 'whatsapp', 'wifi', 'upi', 'menu', 'leads', 'feedback'].map(m => (
                  <button key={m} onClick={() => { if ((m === 'leads' || m === 'feedback' || m === 'wifi' || m === 'upi' || m === 'menu' || m === 'vcard' || m === 'whatsapp') && !limits?.leads) { setShowUpgrade(true); return; } setQrMode(m); }}
                    className={`flex-1 py-2 px-3 rounded-lg text-[9px] font-black uppercase whitespace-nowrap ${qrMode === m ? "bg-slate-700 text-white" : "text-gray-500"}`}>{m}</button>
                ))}
              </div>
              <input type="text" placeholder="Campaign Name" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} className="w-full bg-slate-900/50 p-4 rounded-xl text-white text-xs border border-white/5" />
              {qrMode !== 'vcard' && (
                <div>
                  <label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Center Branding Logo</label>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="w-full text-[10px]" />
                  {logo && <p className="text-[9px] text-green-400 px-1 mt-1">✓ Logo loaded — shows in preview and download</p>}
                </div>
              )}
              {(qrMode === 'direct' || qrMode === 'leads' || qrMode === 'feedback') && (
                <div>
                  <label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Destination URL</label>
                  <input type="url" placeholder="https://your-website.com" value={url} onChange={(e) => setUrl(e.target.value)} disabled={qrType === 'static' && isEditMode}
                    className={`w-full p-4 rounded-xl text-white text-xs border border-white/5 font-mono ${qrType === 'static' && isEditMode ? 'bg-slate-800/50 cursor-not-allowed opacity-50' : 'bg-slate-900/50'}`} />
                  {qrType === 'static' && isEditMode && <p className="text-[9px] text-gray-500 px-1 mt-1">URL cannot be changed for saved Static QR codes</p>}
                </div>
              )}
              {qrMode === 'vcard' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-1 block">Profile Picture</label><input type="file" accept="image/*" onChange={handleProfilePic} className="w-full text-[10px]" /></div>
                  <input type="text" placeholder="First Name" value={vCard.firstName} onChange={(e) => setVCard({ ...vCard, firstName: e.target.value })} className="bg-slate-900/50 p-4 rounded-xl text-white text-xs border border-white/5" />
                  <input type="text" placeholder="Last Name" value={vCard.lastName} onChange={(e) => setVCard({ ...vCard, lastName: e.target.value })} className="bg-slate-900/50 p-4 rounded-xl text-white text-xs border border-white/5" />
                  <input type="email" placeholder="Email" value={vCard.email} onChange={(e) => setVCard({ ...vCard, email: e.target.value })} className="bg-slate-900/50 p-4 rounded-xl text-white text-xs border border-white/5" />
                  <input type="tel" placeholder="Mobile" value={vCard.mobile} onChange={(e) => setVCard({ ...vCard, mobile: e.target.value })} className="bg-slate-900/50 p-4 rounded-xl text-white text-xs border border-white/5" />
                  <input type="url" placeholder="Website" value={vCard.website} onChange={(e) => setVCard({ ...vCard, website: e.target.value })} className="col-span-2 bg-slate-900/50 p-4 rounded-xl text-white text-xs border border-white/5" />
                  <textarea placeholder="Address" value={vCard.address} onChange={(e) => setVCard({ ...vCard, address: e.target.value })} className="col-span-2 bg-slate-900/50 p-4 rounded-xl text-white text-xs border border-white/5" />
                  <div className="col-span-2">
                    <label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Social Media Handles</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['facebook', 'linkedin', 'instagram', 'twitter', 'youtube'].map(social => (
                        <input key={social} type="text" placeholder={social} value={vCard[social]} onChange={(e) => setVCard({ ...vCard, [social]: e.target.value })} className="bg-slate-900/50 p-3 rounded-xl text-white text-[10px] border border-white/5" />
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2 bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                    <p className="text-[9px] text-blue-400 font-black uppercase mb-1">📊 Scan Tracking Enabled</p>
                    <p className="text-[9px] text-gray-400 leading-relaxed">Scanning shows a one-tap screen before saving the contact. Full analytics tracking included.</p>
                  </div>
                </div>
              )}
              {qrMode === 'whatsapp' && (
                <div className="space-y-4">
                  <style>{`.wa-phone .PhoneInputInput { background-color: rgba(255,255,255,0.05) !important; color: white !important; padding: 1rem !important; border-radius: 0 0.75rem 0.75rem 0 !important; border: 1px solid rgba(255,255,255,0.1) !important; border-left: none !important; outline: none !important; font-size: 12px !important; font-family: monospace !important; } .wa-phone .PhoneInputCountry { background: rgba(255,255,255,0.05); padding: 0 12px; border-radius: 0.75rem 0 0 0.75rem; border: 1px solid rgba(255,255,255,0.1); border-right: none; } .wa-phone .PhoneInputInput:focus { border-color: #25d366 !important; } .wa-phone .PhoneInputCountrySelectArrow { color: white; }`}</style>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">WhatsApp Number</label>
                    <PhoneInput international defaultCountry="IN" value={waData.number} onChange={(val) => setWaData({ ...waData, number: val || "" })} placeholder="Enter mobile number" className="wa-phone" />
                    <p className="text-[9px] text-gray-500 px-1 mt-1">Country code is added automatically</p>
                  </div>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Pre-filled Message (Optional)</label>
                    <textarea placeholder="Hi, I'd like to know more about..." value={waData.message} onChange={(e) => setWaData({ ...waData, message: e.target.value })} className="w-full bg-slate-900/50 p-4 rounded-xl text-white text-xs border border-white/5 h-24 outline-none focus:border-green-500 transition-all" />
                  </div>
                </div>
              )}
              {qrMode === 'wifi' && (
                <div className="space-y-4">
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Network Name (SSID)</label><input type="text" placeholder="MyWiFi" value={wifiData.ssid} onChange={(e) => setWifiData({ ...wifiData, ssid: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Password</label><input type="password" placeholder="••••••••" value={wifiData.password} onChange={(e) => setWifiData({ ...wifiData, password: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Security</label>
                    <select value={wifiData.security} onChange={(e) => setWifiData({ ...wifiData, security: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none">
                      <option value="WPA">WPA/WPA2</option><option value="WEP">WEP</option><option value="nopass">No Password</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={wifiData.hidden} onChange={(e) => setWifiData({ ...wifiData, hidden: e.target.checked })} className="rounded" /><span className="text-[10px] text-gray-400">Hidden Network</span></label>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                    <p className="text-[9px] text-blue-400 font-black uppercase mb-1">📊 Scan Tracking Enabled</p>
                    <p className="text-[9px] text-gray-400 leading-relaxed">Scanning shows a one-tap screen before connecting to WiFi. Full analytics tracking included.</p>
                  </div>
                </div>
              )}
              {qrMode === 'upi' && (
                <div className="space-y-4">
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">UPI ID</label><input type="text" placeholder="yourname@upi" value={upiData.id} onChange={(e) => setUpiData({ ...upiData, id: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Display Name</label><input type="text" placeholder="Your Name/Business" value={upiData.name} onChange={(e) => setUpiData({ ...upiData, name: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-2 block">Fixed Amount ₹ (optional)</label><input type="number" placeholder="e.g. 99" value={upiData.amount} onChange={(e) => setUpiData({ ...upiData, amount: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" /></div>
                  <p className="text-[9px] text-gray-500 px-1">Works with PhonePe, GPay, Paytm and all UPI apps</p>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                    <p className="text-[9px] text-blue-400 font-black uppercase mb-1">📊 Scan Tracking Enabled</p>
                    <p className="text-[9px] text-gray-400 leading-relaxed">Scanning shows a one-tap screen before opening your UPI app. Full analytics tracking included.</p>
                  </div>
                </div>
              )}
              {qrMode === 'menu' && (
                <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-xl space-y-3">
                  <p className="text-[10px] text-blue-400 font-black">📋 Smart Menu QR</p>
                  <p className="text-[9px] text-gray-400">This QR will open your digital menu when scanned. After saving, go to Menu Builder in the sidebar to add categories and items.</p>
                  <input type="text" placeholder="Menu Name" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" />
                </div>
              )}

              {(qrMode === "direct" || qrMode === "leads" || qrMode === "feedback") && (
                <div className="bg-black/20 p-4 rounded-xl border border-white/5 space-y-3">
                  <p className="text-[9px] text-red-500 uppercase font-black px-1 tracking-widest italic">Retargeting Pixels</p>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-1 block">Meta (Facebook) Pixel ID</label>
                    <input type="text" placeholder="e.g. 123456789012345" value={pixels.fb} onChange={(e) => setPixels({ ...pixels, fb: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-1 block">Google Tag / Pixel ID</label>
                    <input type="text" placeholder="e.g. AW-123456789" value={pixels.google} onChange={(e) => setPixels({ ...pixels, google: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" />
                  </div>
                </div>
              )}

              <div className="bg-black/20 p-4 rounded-xl border border-white/5 space-y-3">
                <p className="text-[9px] text-red-500 uppercase font-black px-1 tracking-widest italic">PIN Protection</p>
                <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-1 block">Set PIN Code (leave blank to disable)</label>
                  <input type="password" placeholder="e.g. 1234" value={pinCode} onChange={(e) => setPinCode(e.target.value)} maxLength={8} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" />
                  <p className="text-[9px] text-gray-600 px-1 mt-1">Scanners must enter this PIN before accessing the content</p>
                </div>
              </div>

              {qrMode === "direct" && (
                <div className="bg-black/20 p-4 rounded-xl border border-white/5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div><p className="text-[9px] text-red-500 uppercase font-black px-1 tracking-widest italic">Dynamic Coupons</p><p className="text-[9px] text-gray-600 px-1 mt-1">Show unique coupon codes on each scan</p></div>
                    <button type="button" onClick={() => setCouponMode(!couponMode)} className={`w-10 h-5 rounded-full transition-all relative ${couponMode ? "bg-blue-500" : "bg-white/10"}`}><div className="absolute w-4 h-4 bg-white rounded-full transition-all" style={{ top: "2px", left: couponMode ? "20px" : "2px" }} /></button>
                  </div>
                  {couponMode && <p className="text-[9px] text-blue-400 px-1">Upload coupon codes in Coupon Manager after saving this QR.</p>}
                </div>
              )}

              {qrMode === "direct" && (
                <div className="bg-black/20 p-4 rounded-xl border border-white/5 space-y-3">
                  <p className="text-[9px] text-red-500 uppercase font-black px-1 tracking-widest italic">A/B Split Testing</p>
                  <button type="button" onClick={() => setAbTesting(!abTesting)} className={`w-10 h-5 rounded-full transition-all relative ${abTesting ? "bg-blue-500" : "bg-white/10"}`}><div className="absolute w-4 h-4 bg-white rounded-full transition-all" style={{ top: "2px", left: abTesting ? "20px" : "2px" }} /></button>
                  {abTesting && (
                    <div className="space-y-2">
                      <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-1 block">URL A (50% of scans)</label>
                        <input type="text" placeholder="https://version-a.com" value={abUrls.a} onChange={(e) => setAbUrls({ ...abUrls, a: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" />
                      </div>
                      <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-1 block">URL B (50% of scans)</label>
                        <input type="text" placeholder="https://version-b.com" value={abUrls.b} onChange={(e) => setAbUrls({ ...abUrls, b: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" />
                      </div>
                      <p className="text-[9px] text-gray-600 px-1">Traffic will be split 50/50 between both URLs randomly</p>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* ===== STYLE TAB ===== */}
          {activeTab === 'style' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-[10px] text-red-500 uppercase font-black tracking-widest italic">Style</p>
                <BrandKit user={user} onApply={(kit) => { setDotsColor(kit.dots_color || dotsColor); setBgColor(kit.bg_color || bgColor); setEyesColor(kit.eyes_color || eyesColor); setDotsType(kit.dots_type || dotsType); setCornersType(kit.corners_type || cornersType); }} />
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <ColorInput label="Dots" caption="Pattern" value={dotsColor} onChange={setDotsColor} dataKit="dots" />
                  <ColorInput label="Background" caption="Backdrop" value={bgColor} onChange={setBgColor} dataKit="bg" />
                  <ColorInput label="Eyes" caption="Outer" value={eyesColor} onChange={setEyesColor} dataKit="eyes" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <select value={dotsType} onChange={(e) => setDotsType(e.target.value)} className="bg-black border border-white/10 p-3 rounded-xl text-white text-[10px] font-black uppercase">
                    <option value="square">Square</option><option value="dots">Dots</option><option value="rounded">Rounded</option><option value="classy">Classy</option><option value="fluid">Fluid</option>
                  </select>
                  <select value={cornersType} onChange={(e) => setCornersType(e.target.value)} className="bg-black border border-white/10 p-3 rounded-xl text-white text-[10px] font-black uppercase">
                    <option value="square">Sq Eye</option><option value="extra-rounded">Rd Eye</option>
                  </select>
                  <input type="file" onChange={handleLogoUpload} className="hidden" id="logo-up" />
                  <label htmlFor="logo-up" className="bg-red-600 text-center py-3 rounded-xl text-[10px] font-black uppercase cursor-pointer">Logo</label>
                </div>
              </div>
            </div>
          )}

          {/* ===== DESIGNER TAB ===== */}
          {activeTab === 'designer' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="bg-black/20 p-6 rounded-3xl border border-white/5 space-y-4">
                <p className="text-[10px] font-black text-red-500 uppercase tracking-widest">Frames & Shapes</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><label className="text-[9px] text-gray-500 uppercase font-black">Frame Type</label>
                    <select value={designer.frame} onChange={(e) => setDesigner({ ...designer, frame: e.target.value })} className="w-full bg-black border border-white/10 p-3 rounded-xl text-white text-[10px] font-black uppercase">
                      <option value="none">No Frame</option><option value="bubble">Bubble Text</option><option value="label">Modern Label</option><option value="sleek">Sleek Border</option>
                    </select>
                  </div>
                  <div className="space-y-2"><label className="text-[9px] text-gray-500 uppercase font-black">QR Shape</label>
                    <select value={designer.qrShape} onChange={(e) => setDesigner({ ...designer, qrShape: e.target.value })} className="w-full bg-black border border-white/10 p-3 rounded-xl text-white text-[10px] font-black uppercase">
                      <option value="square">Classic Square</option><option value="circle">Circular QR</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2"><label className="text-[9px] text-gray-500 uppercase font-black">Frame Text</label>
                  <input type="text" value={designer.frameText} onChange={(e) => setDesigner({ ...designer, frameText: e.target.value })} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-[10px] border border-white/5" />
                </div>
                <div className="flex gap-4">
                  <ColorInput label="Frame" caption="Outer Fill" value={designer.frameColor} onChange={(v) => setDesigner({ ...designer, frameColor: v })} />
                  <ColorInput label="Text" caption="Font Color" value={designer.textColor} onChange={(v) => setDesigner({ ...designer, textColor: v })} />
                  <div className="space-y-2 flex-1"><label className="text-[9px] text-gray-500 uppercase font-black">Border Design</label>
                    <select value={designer.borderStyle} onChange={(e) => setDesigner({ ...designer, borderStyle: e.target.value })} className="w-full bg-black border border-white/10 p-2.5 rounded-xl text-white text-[10px] font-black uppercase">
                      <option value="solid">Single (Solid)</option><option value="double">Double Line</option><option value="dotted">Dotted</option><option value="dashed">Dashed</option>
                    </select>
                  </div>
                </div>
                {designer.frame !== "none" && (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                    <p className="text-[9px] text-blue-400 font-black uppercase mb-1">✓ Frame + Logo Export</p>
                    <p className="text-[9px] text-gray-400">Frame and logo are both rendered natively on export — no html2canvas. PNG, JPG, WEBP and PDF all include the frame.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== ADVANCED TAB ===== */}
          {activeTab === 'advanced' && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
              <div className="bg-black/20 p-6 rounded-3xl border border-white/5 space-y-3">
                <p className="text-[10px] font-black text-orange-500 uppercase">Routing & Pixels</p>
                <div className="bg-black/20 p-4 rounded-xl border border-white/5 space-y-3">
                  <p className="text-[9px] text-red-500 uppercase font-black px-1 tracking-widest italic">QR Expiry Settings</p>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-1 block">Expire After Date (optional)</label>
                    <input type="datetime-local" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-[9px] text-gray-500 uppercase font-black px-1 mb-1 block">Max Scans (optional)</label>
                    <input type="number" placeholder="e.g. 100" value={maxScans} onChange={e => setMaxScans(e.target.value)} className="w-full bg-slate-900/50 p-3 rounded-xl text-white text-xs border border-white/5 outline-none focus:border-blue-500" />
                    <p className="text-[9px] text-gray-600 px-1 mt-1">QR deactivates after this many scans</p>
                  </div>
                </div>
                <div className="mt-6 space-y-4 border-t border-white/10 pt-4">
                  <p className="text-[10px] font-black text-red-500 uppercase">Time Based Routing</p>
                  <button onClick={() => setTimeRoutes([...timeRoutes, { start_time: "", end_time: "", destination_url: "", country_code: "", device: "" }])} className="bg-red-600 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase">+ Add Time Slot</button>
                  {timeRoutes.map((slot, index) => (
                    <div key={index} className="bg-slate-900/50 p-4 rounded-xl border border-white/5 space-y-3">
                      <input type="text" placeholder="Destination URL" value={slot.destination_url} onChange={(e) => setTimeRoutes(prev => prev.map((item, i) => i === index ? { ...item, destination_url: e.target.value } : item))} className="w-full p-2 bg-black text-white text-[10px] rounded" />
                      <div className="flex gap-2">
                        <input type="time" value={slot.start_time || ""} onChange={(e) => setTimeRoutes(prev => prev.map((item, i) => i === index ? { ...item, start_time: e.target.value } : item))} className="w-1/2 p-2 bg-black text-white text-[10px] rounded" />
                        <input type="time" value={slot.end_time || ""} onChange={(e) => setTimeRoutes(prev => prev.map((item, i) => i === index ? { ...item, end_time: e.target.value } : item))} className="w-1/2 p-2 bg-black text-white text-[10px] rounded" />
                      </div>
                      <div className="space-y-2"><p className="text-[9px] text-gray-400 uppercase">Timezone</p>
                        <select value={slot.timezone || ""} onChange={(e) => setTimeRoutes(prev => prev.map((item, i) => i === index ? { ...item, timezone: e.target.value } : item))} className="w-full p-2 bg-black text-white text-[10px] rounded">
                          <option value="">Select Timezone</option>
                          {(slot.country_code ? moment.tz.zonesForCountry(slot.country_code) : moment.tz.names()).map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
                        </select>
                      </div>
                      <div className="space-y-2"><p className="text-[9px] text-gray-400 uppercase">Target Device</p>
                        <select value={slot.device || ""} onChange={(e) => setTimeRoutes(prev => prev.map((item, i) => i === index ? { ...item, device: e.target.value } : item))} className="w-full p-2 bg-black text-white text-[10px] rounded">
                          <option value="">All Devices</option><option value="ios">iOS</option><option value="android">Android</option>
                        </select>
                      </div>
                      <button onClick={() => setTimeRoutes(prev => prev.filter((_, i) => i !== index))} className="text-red-500 text-[10px] font-bold mt-2">Remove</button>
                    </div>
                  ))}
                  <div className="border-t border-white/10 pt-4 space-y-2">
                    <p className="text-[10px] text-gray-400 uppercase font-black">After Hours Behavior</p>
                    <select value={fallback.type} onChange={(e) => setFallback({ ...fallback, type: e.target.value })} className="w-full bg-black text-white p-3 rounded-lg text-[10px]">
                      <option value="url">Redirect to URL</option><option value="message">Show Message</option>
                    </select>
                    <input type="text" placeholder="Fallback" value={fallback.value} onChange={(e) => setFallback({ ...fallback, value: e.target.value })} className="w-full p-3 bg-black text-white rounded-lg text-[10px]" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── PERSISTENT SAVE & UPGRADE — visible on ALL tabs ── */}
          <div className="space-y-3 pt-2">
            {loading && (
              <div className="flex items-center justify-center gap-2 py-1">
                <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest animate-pulse">Saving... please wait</p>
              </div>
            )}
            <button
              onClick={generateAndSave}
              disabled={loading || (qrMode === "upi" && !upiData.id?.trim())}
              style={{ letterSpacing: "0.3em" }}
              className="w-full bg-red-600 hover:bg-red-500 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-4 rounded-2xl uppercase shadow-xl transition-all"
            >
              {loading ? "SAVING..." : isEditMode ? "UPDATE QR" : "SAVE YOUR QR"}
            </button>
            <button
              onClick={() => setShowUpgrade(true)}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 active:scale-95 text-white font-black py-4 rounded-2xl uppercase transition-all"
            >
              UPGRADE TO PRO
            </button>
          </div>

        </div>

        {/* RIGHT COLUMN — PREVIEW */}
        <div className="flex flex-col gap-8 lg:sticky lg:top-10 h-fit lg:w-96">
          <div id="qr-preview-container" className="bg-transparent flex flex-col items-center justify-center p-8">
            <div className="inline-block transition-all duration-500">

              {/* QR CODE — frame border on top/left/right only when frame is active */}
              <div
                className={`bg-white shadow-2xl flex items-center justify-center transition-all duration-500 ${designer.qrShape === "circle" ? "rounded-full" : ""}`}
                style={{
                  lineHeight: 0,
                  padding: "20px",
                  borderRadius: designer.qrShape === "circle" ? "50%" :
                    designer.frame !== "none" ? (designer.frame === "sleek" ? "6px 6px 0 0" : "20px 20px 0 0") : "20px",
                  border: designer.frame !== "none"
                    ? `${designer.frame === "sleek" ? "3px" : "6px"} solid ${designer.frameColor || "#000000"}`
                    : "none",
                  borderBottom: designer.frame !== "none" && designer.qrShape !== "circle" ? "none" : undefined,
                }}
              >
                <div ref={ref} className="flex items-center justify-center" />
              </div>

              {/* FRAME TEXT BANNER — BELOW the QR, never overlaps it */}
              {designer.frame !== "none" && (
                <div
                  style={{
                    backgroundColor: designer.frameColor || "#000000",
                    border: `${designer.frame === "sleek" ? "3px" : "6px"} solid ${designer.frameColor || "#000000"}`,
                    borderTop: "none",
                    borderRadius: designer.frame === "sleek" ? "0 0 6px 6px" : "0 0 20px 20px",
                    padding: designer.frame === "bubble" ? "10px 16px 14px" : "8px 16px 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  {designer.frame === "bubble" && <span style={{ fontSize: 14 }}>📷</span>}
                  <span style={{
                    color: designer.textColor || "#ffffff",
                    fontWeight: 900,
                    fontSize: designer.frame === "sleek" ? 10 : 12,
                    textTransform: "uppercase",
                    letterSpacing: designer.frame === "sleek" ? "3px" : "2px",
                  }}>
                    {designer.frameText || "SCAN ME"}
                  </span>
                  {designer.frame === "bubble" && <span style={{ fontSize: 14 }}>📷</span>}
                </div>
              )}
            </div>
            {logo && <p className="text-[9px] text-green-400 mt-3 text-center">✓ Logo active — included in all downloads</p>}
          </div>
          <div className="bg-black/40 border border-white/10 p-6 rounded-[32px] space-y-6">
            <div className="space-y-3">
              <p className="text-[10px] font-black text-red-500 uppercase tracking-widest text-center">Export Resolution</p>
              <div className="grid grid-cols-4 gap-2">
                {[512, 1024, 2048, 4096].map(size => (
                  <button key={size} onClick={() => setDesigner({ ...designer, exportSize: size })} className={`py-2 rounded-lg text-[10px] font-bold transition-all ${designer.exportSize === size ? "bg-red-600 text-white" : "bg-slate-800 text-gray-400"}`}>{size}px</button>
                ))}
              </div>
            </div>
            <div className="space-y-3 border-t border-white/5 pt-4">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest text-center">Download Format</p>
              <div className="grid grid-cols-5 gap-2">
                {["png", "jpg", "svg", "webp"].map(ext => (
                  <button key={ext} onClick={() => downloadQR(ext)} disabled={loading} className="bg-slate-800 py-3 rounded-xl text-[9px] font-black text-white hover:bg-red-600 uppercase transition-all disabled:opacity-50">{ext}</button>
                ))}
                <button onClick={downloadPDF} disabled={loading} className="bg-slate-800 py-3 rounded-xl text-[9px] font-black text-white hover:bg-red-600 uppercase transition-all disabled:opacity-50">PDF</button>
              </div>
              {loading && <p className="text-[9px] text-blue-400 text-center animate-pulse">Generating export...</p>}
            </div>
          </div>
        </div>
      </div>
      {showUpgrade && <UpgradeModal user={user} onClose={() => setShowUpgrade(false)} onSuccess={(newPlan) => { addToast(`Upgraded to ${newPlan}!`, "success"); }} />}
    </div>
  );
}
